import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageBar, MessageBarBody, Text } from '@fluentui/react-components';
import GlMapEditor from '../components/GlMapEditor';
import Instructions from '../components/Instructions';
import { usePageStyles } from '../components/layout/pageStyles';

/**
 * The standalone G/L mapping screen — bulk setup.
 *
 * <p>§8.2 keeps this alongside the client page's G/L tab deliberately: the tab answers
 * "what is this client's mapping?" in context, this screen is for working through several
 * clients' mappings in a row. Both now render the SAME <see cref="GlMapEditor"/>, so there
 * is one implementation of an editable form over these records rather than two that could
 * drift — with editable data, duplication is a correctness hazard, not merely repetition.</p>
 *
 * <p>The client moved into `?clientId=` (§9), which also makes a mapping linkable: the same
 * URL contract as every other C7 screen.</p>
 */
export default function GlMappingPage() {
  const frame = usePageStyles();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const activeClientId = searchParams.get('clientId') ?? '';

  // The client is picked in the HEADER now (docs/client-context.md): one picker, every screen.

  return (
    <div className={frame.page}>
      <div className={frame.header}>
        <Text as="h1" className={frame.title}>{t('glMappingPage.title')}</Text>
        <Text className={frame.subtitle}>{t('glMappingPage.subtitle')}</Text>
      </div>

      <Instructions screen="glMappings" />

      {/*
        CONVERSIONS_SPEC.md §3: the GUID field is gone. Picking a client from the name lookup IS the load: there is no
        second button to press. (This sat in a card of its own, which stayed on screen EMPTY once a client was picked;
        the polish pass of 2026-10-28 removed the card.)
      */}
      {!activeClientId && (
        <MessageBar intent="info">
          <MessageBarBody>{t('clientContext.chooseInHeader')}</MessageBarBody>
        </MessageBar>
      )}

      {activeClientId && (
        <div className="card">
          <GlMapEditor clientId={activeClientId} />
        </div>
      )}
    </div>
  );
}
